import z from 'zod';

import { getElementTypes } from '@@plate/utils/getElementTypes';

import { PlateTextElements, TextElementsCodec, UnityTextElements } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, UNITY_ELEMENT_TYPES } from '../constants';

const UnityCrossheadType = {
    DEFAULT: '_crosshead_default',
    SUBSECTION: '_crosshead_subsection',
    LISTICLE_ORDERED: '_crosshead_listicle',
    LISTICLE_UNORDERED: '_crosshead_listicle_unordered',
} as const;

const UnityCrossheadTypeSchema = z.enum(UnityCrossheadType);
type UnityCrossheadType = z.infer<typeof UnityCrossheadTypeSchema>;

const UnityCrossheadStyleSchema = z.object({
    crossheadType: UnityCrossheadTypeSchema.optional(),
});

export const UnityCrosshead = z.object({
    type: z.literal(UNITY_ELEMENT_TYPES.CROSSHEAD),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    style: UnityCrossheadStyleSchema.nullable(),
    items: UnityTextElements,
});
export type UnityCrosshead = z.infer<typeof UnityCrosshead>;

const PlateCrossheadBase = z.object({
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});

export const PlateCrossheadDefault = z.object({
    ...PlateCrossheadBase.shape,
    type: z.literal(ELEMENT_TYPES.CROSSHEAD_DEFAULT),
});
export const PlateCrossheadSubsection = z.object({
    ...PlateCrossheadBase.shape,
    type: z.literal(ELEMENT_TYPES.CROSSHEAD_SUBSECTION),
});
export const PlateCrossheadOrderedListicle = z.object({
    ...PlateCrossheadBase.shape,
    type: z.literal(ELEMENT_TYPES.CROSSHEAD_LISTICLE_ORDERED),
});
export const PlateCrossheadUnorderedListicle = z.object({
    ...PlateCrossheadBase.shape,
    type: z.literal(ELEMENT_TYPES.CROSSHEAD_LISTICLE_UNORDERED),
});

export const PlateAnyCrosshead = z.union([
    PlateCrossheadDefault,
    PlateCrossheadSubsection,
    PlateCrossheadOrderedListicle,
    PlateCrossheadUnorderedListicle,
]);

export type CrossheadDefaultElement = z.infer<typeof PlateCrossheadDefault>;
export type CrossheadSubsectionElement = z.infer<typeof PlateCrossheadSubsection>;
export type CrossheadOrderedListicleElement = z.infer<typeof PlateCrossheadOrderedListicle>;
export type CrossheadUnorderedListicleElement = z.infer<typeof PlateCrossheadUnorderedListicle>;
export type AnyCrossheadElement = z.infer<typeof PlateAnyCrosshead>;

export const ANY_CROSSHEAD_ELEMENT_TYPES = getElementTypes([PlateAnyCrosshead]);
export const CROSSHEAD_SUBSECTION_ELEMENT_TYPES = getElementTypes([PlateCrossheadSubsection]);

const resolveCrossheadElementType = (
    crossheadType: UnityCrossheadType,
): AnyCrossheadElement['type'] => {
    switch (crossheadType) {
        case UnityCrossheadType.DEFAULT:
            return ELEMENT_TYPES.CROSSHEAD_DEFAULT;
        case UnityCrossheadType.SUBSECTION:
            return ELEMENT_TYPES.CROSSHEAD_SUBSECTION;
        case UnityCrossheadType.LISTICLE_ORDERED:
            return ELEMENT_TYPES.CROSSHEAD_LISTICLE_ORDERED;
        case UnityCrossheadType.LISTICLE_UNORDERED:
            return ELEMENT_TYPES.CROSSHEAD_LISTICLE_UNORDERED;
    }
};

const resolveUnityCrossheadType = (
    elementType: AnyCrossheadElement['type'],
): UnityCrossheadType => {
    switch (elementType) {
        case ELEMENT_TYPES.CROSSHEAD_DEFAULT:
            return UnityCrossheadType.DEFAULT;
        case ELEMENT_TYPES.CROSSHEAD_SUBSECTION:
            return UnityCrossheadType.SUBSECTION;
        case ELEMENT_TYPES.CROSSHEAD_LISTICLE_ORDERED:
            return UnityCrossheadType.LISTICLE_ORDERED;
        case ELEMENT_TYPES.CROSSHEAD_LISTICLE_UNORDERED:
            return UnityCrossheadType.LISTICLE_UNORDERED;
    }
};

export const CrossheadCodec = z.codec(UnityCrosshead, PlateAnyCrosshead, {
    decode: (value) =>
        ({
            type: resolveCrossheadElementType(
                value.style?.crossheadType ?? UnityCrossheadType.DEFAULT,
            ),
            id: value.elementId ?? crypto.randomUUID(),
            ...(value.templateElement && { data: { templateElement: value.templateElement } }),
            children: TextElementsCodec.decode(value.items),
        }) satisfies AnyCrossheadElement,
    encode: (value) =>
        ({
            type: UNITY_ELEMENT_TYPES.CROSSHEAD,
            elementId: value.id,
            version: CURRENT_VERSION,
            items: TextElementsCodec.encode(value.children),
            style: { crossheadType: resolveUnityCrossheadType(value.type) },
            ...value.data,
        }) satisfies UnityCrosshead,
});
